import { useMemo, useCallback } from 'react';
import { Formalite } from '../services/baserowAPI';
import { useFormalitesCache } from '../contexts/FormalitesContext';

interface FilterConfig {
  field_7261?: string; // Entreprise
  field_7260?: string; // Client
  field_7263?: string; // Type de formalité
  field_7265?: string; // Progression
}

interface UseFormalitesFilterResult {
  data: Formalite[];
  allData: Formalite[];
  loading: boolean;
  error: string | null;
  totalCount: number;
  filteredCount: number;
  createRow: (data: Partial<Formalite>) => Promise<Formalite | null>;
  updateRow: (rowId: number, data: Partial<Formalite>) => Promise<Formalite | null>;
  deleteRow: (rowId: number) => Promise<boolean>;
  refresh: () => Promise<void>;
}

export function useFormalitesFilter(
  filters: FilterConfig,
  sortField: string | null,
  sortDirection: 'asc' | 'desc' | null
): UseFormalitesFilterResult {
  const { allData, loading, error, createRow, updateRow, deleteRow, refresh } = useFormalitesCache();

  // Filtrage côté client
  const filteredData = useMemo(() => {
    let result = [...allData];

    // Appliquer les filtres
    if (filters.field_7261) {
      const searchTerm = filters.field_7261.toLowerCase();
      result = result.filter(item => {
        const companies = item.field_7261 || [];
        return companies.some(company => 
          company.value?.toLowerCase().includes(searchTerm)
        );
      });
    }

    if (filters.field_7260) {
      const searchTerm = filters.field_7260.toLowerCase();
      result = result.filter(item => {
        const clients = item.field_7260 || [];
        return clients.some(client => 
          client.value?.toLowerCase().includes(searchTerm)
        );
      });
    }

    if (filters.field_7263) {
      const searchTerm = filters.field_7263.toLowerCase();
      result = result.filter(item => {
        const types = item.field_7263 || [];
        return types.some(type => 
          type.value?.toLowerCase().includes(searchTerm)
        );
      });
    }

    if (filters.field_7265) {
      const searchTerm = filters.field_7265.toLowerCase();
      result = result.filter(item => {
        const progression = item.field_7265 || '';
        return progression.toLowerCase().includes(searchTerm);
      });
    }

    // Appliquer le tri
    if (sortField && sortDirection) {
      result.sort((a, b) => {
        const aValue = (a as any)[sortField];
        const bValue = (b as any)[sortField];

        // Gérer les valeurs nulles/undefined
        if (aValue == null && bValue == null) return 0;
        if (aValue == null) return sortDirection === 'asc' ? -1 : 1;
        if (bValue == null) return sortDirection === 'asc' ? 1 : -1;

        // Gérer les tableaux (link_row)
        if (Array.isArray(aValue) && Array.isArray(bValue)) {
          const aStr = aValue.map(v => v.value || '').join(', ').toLowerCase();
          const bStr = bValue.map(v => v.value || '').join(', ').toLowerCase();
          return sortDirection === 'asc' 
            ? aStr.localeCompare(bStr, 'fr')
            : bStr.localeCompare(aStr, 'fr');
        }

        // Gérer les dates
        if (sortField.startsWith('field_726') && (sortField === 'field_7264' || sortField === 'field_7269' || sortField === 'field_7270' || sortField === 'field_7271')) {
          const aDate = new Date(aValue).getTime();
          const bDate = new Date(bValue).getTime();
          return sortDirection === 'asc' ? aDate - bDate : bDate - aDate;
        }

        // Gérer les chaînes de caractères
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return sortDirection === 'asc'
            ? aValue.localeCompare(bValue, 'fr')
            : bValue.localeCompare(aValue, 'fr');
        }

        // Fallback pour nombres et autres types
        return sortDirection === 'asc' 
          ? aValue > bValue ? 1 : -1
          : aValue < bValue ? 1 : -1;
      });
    }

    return result;
  }, [allData, filters, sortField, sortDirection]);

  return {
    data: filteredData,
    allData,
    loading,
    error,
    totalCount: allData.length,
    filteredCount: filteredData.length,
    createRow,
    updateRow,
    deleteRow,
    refresh,
  };
}
